import z from 'zod';
import {
  isDuplicateMention,
  isInternalAiNote,
  isNotCommunicated,
  stripAiReflections,
  stripDatesAndMetadataFromTitle,
} from '../utils/cv';
import {
  CV,
  Education,
  Experience,
  Language,
  Locale,
  PersonalExperience,
  Prisma,
} from './generated';

export const cvLanguageSchema = z.object({
  language: z.string(),
  level: z.string().describe('The level of the language'),
});

export const cvSensitiveInfoSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().nullable(),
  phone: z.string(),
});

export type CVSensitiveInfo = z.infer<typeof cvSensitiveInfoSchema>;

export const jsonValueSchema: z.ZodType<Prisma.JsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(jsonValueSchema),
    z.record(z.string(), jsonValueSchema),
  ]),
);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isRenderPayload(value: unknown): value is FullCVWithTemplate {
  if (!isRecord(value)) {
    return false;
  }

  const locale = value.locale;
  if (!isRecord(locale)) {
    return false;
  }

  const templateParams = jsonValueSchema.safeParse(value.templateParams);
  return (
    typeof value.interviewId === 'string' &&
    typeof value.template === 'string' &&
    templateParams.success &&
    typeof locale.name === 'string' &&
    typeof locale.codeShort === 'string' &&
    typeof locale.codeLong === 'string'
  );
}

export const renderCvPayloadSchema = z.custom<FullCVWithTemplate>(
  value => isRenderPayload(value),
  'Invalid render payload. Expected FullCVWithTemplate JSON.',
);

export type RenderCvPayload = z.infer<typeof renderCvPayloadSchema>;

export function parseRenderCvPayload(payload: unknown): RenderCvPayload {
  return renderCvPayloadSchema.parse(payload);
}

const cvPersonalInfoSchema = z.object({
  postalCode: z.string().nullable().describe('Postal code of the user'),
  location: z.string().nullable().describe('City of the user'),
  jobTitle: z.string().describe('Job title of the user'),
  hasDriverLicense: z.boolean().describe('Whether the user has a driver license or not'),
  driverLicenseTypes: z
    .string()
    .nullable()
    .describe(
      'Types of driver license the user has (A, AM, B, C, D, E, etc.). Required if the user has a driver license. Prefix with "Permis" according to the language of the user, if he didn\'t already.',
    ),
  hobbiesAndInterests: z.array(z.string()).nullable().describe('Hobbies and interests of the user'),
  openToTraining: z.boolean().nullable().default(false),
  gender: z.string().nullable().describe('Gender of the user (homme/femme) for text inflection'),
  mobility: z.object({
    isMobile: z.boolean().describe('Whether the user is mobile or not'),
    range: z
      .string()
      .nullable()
      .describe('Range or cities the user is willing to move to (optional)'),
    radius: z.number().nullable().describe('Radius of the mobility in km around the location'),
  }),
});

const cvExperienceSchema = z.object({
  company: z
    .string()
    .describe(
      'Name of the company where the user worked. Preserve proper nouns and official company names as-is. NEVER include dates, job titles, or locations here.',
    ),
  location: z.string().describe('City where the user worked. NEVER include dates, company names, or job titles here.'),
  title: z
    .string()
    .describe(
      'Job title strictly. NEVER include dates, years, periods (e.g. do NOT write "Technicien 2020-2022" or "(depuis 2021)"), companies, or locations in this field as there are dedicated fields for them.',
    ),
  missions: z
    .string()
    .describe(
      'Mission, responsibilities and achievements of the user in the company. IMPORTANT: Use infinitive form without pronouns (e.g. "Développer des applications web" not "J\'ai développé des applications web"). Never use first-person conjugated sentences.',
    ),
  startDate: z
    .string()
    .nullable()
    .describe(
      'The start date of the experience, formatted as YYYY-MM, or YYYY alone when only the year is known. NEVER invent a month.',
    ),
  endDate: z
    .string()
    .nullable()
    .describe(
      'The end date of the experience, formatted as YYYY-MM (or YYYY alone when only the year is known) or null if it is the current experience (optional). NEVER invent a month.',
    ),
});

const cvPersonalExperienceSchema = z.object({
  description: z.string().nullable().describe('Description of the personal experience'),
  title: z
    .string()
    .describe(
      'Title of the personal experience strictly. NEVER include dates, years, or periods in this field.',
    ),
  location: z
    .string()
    .nullable()
    .describe('City where the personal experience took place, only if explicitly stated. Null when unknown.'),
  startDate: z
    .string()
    .nullable()
    .describe(
      'The start date of the personal experience, formatted as YYYY-MM, or YYYY alone when only the year is known. NEVER invent a month.',
    ),
  endDate: z
    .string()
    .nullable()
    .describe(
      'The end date of the personal experience, formatted as YYYY-MM (or YYYY alone when only the year is known) or null if it is the current experience (optional). NEVER invent a month.',
    ),
});

const cvExperiencesSchema = z.object({
  professional: z.array(cvExperienceSchema).describe("The user's professional experiences."),
  personal: z
    .array(cvPersonalExperienceSchema)
    .describe("The user's personal experiences (projects, associations, etc.)."),
});

const cvSkillsSchema = z.object({
  skills: z.array(z.string()).describe('Skills of the user, both hard and soft'),
  strengths: z
    .array(z.string())
    .describe('Strengths of the user, how his/her colleagues would describe him/her'),
  preferedTasks: z
    .array(z.string())
    .describe('Tasks the user enjoys the most. Use infinitive form without pronouns.'),
  notPreferedTasks: z
    .array(z.string())
    .describe('Tasks the user enjoys the least. Use infinitive form without pronouns.'),
  languages: z.array(cvLanguageSchema).describe('Languages the user speaks'),
});

const cvSchoolSchema = z.object({
  degree: z
    .string()
    .nullable()
    .describe(
      'Degree type of the user (e.g. CAP, BEP, BAC, BTS, Licence, Master, etc.), null if no degree is mentioned. NEVER include dates, school names, or years in this field.',
    ),
  school: z
    .string()
    .describe(
      'Name of the school. Preserve proper nouns and official names as-is (e.g. "Université de Nantes", "MIT"). Only translate the language of the name if it has an official French equivalent. NEVER include dates or diploma names here.',
    ),
  name: z
    .string()
    .describe(
      'Name of the training/formation program / diploma strictly. NEVER include dates, years, periods (e.g. do NOT write "Électricité 2018-2020"), or school names here.',
    ),
  acquiredSkills: z
    .array(z.string())
    .nullable()
    .describe(
      'Description of the skills acquired at the school. Use infinitive form without pronouns.',
    ),
  location: z
    .string()
    .nullable()
    .describe('City of the school, only if explicitly stated by the candidate. Null when unknown.'),
  degreeStartedAt: z
    .string()
    .nullable()
    .describe(
      'Start date of the formation, formatted as YYYY-MM, or YYYY alone when only the year is known. NEVER invent a month.',
    ),
  degreeObtainedAt: z
    .string()
    .nullable()
    .describe(
      'Date of the degree acquisition, formatted as YYYY-MM, or YYYY alone when only the year is known. NEVER invent a month.',
    ),
});

const cvCertificationSchema = z.object({
  certification: z.string().nullable().describe('Name of the certification'),
  issuingOrganization: z
    .string()
    .nullable()
    .describe('Name of the organization that issued the certification'),
  certificationObtainedAt: z
    .string()
    .nullable()
    .describe(
      'Date of the certification acquisition, formatted as YYYY-MM, or YYYY alone when only the year is known. NEVER invent a month.',
    ),
  acquiredSkills: z
    .array(z.string())
    .nullable()
    .describe('Specific skills or authorizations this certification grants'),
});

const cvEducationSchema = z.object({
  schools: z.array(cvSchoolSchema).describe('Schools and training centers of the user'),
  certifications: z.array(cvCertificationSchema).describe('Certifications of the user'),
});

export const cvSchema = z
  .object({
    personalInfo: cvPersonalInfoSchema,
    experiences: cvExperiencesSchema,
    skills: cvSkillsSchema,
    education: cvEducationSchema,
    isCompleted: z.boolean().describe('Whether the CV is completed or not'),
    summary: z
      .string()
      .describe(
        'Summary of the CV. Must be strictly factual about the candidate. NEVER include editorial notes, reflections, or uncertainty comments (e.g. do NOT write "à préciser" or "à confirmer").',
      ),
    question: z.string().nullable().describe('Follow up question'),
    isReconversion: z
      .boolean()
      .describe('Whether the candidate is in a career change / reconversion situation.'),
    complementaryInfo: z
      .object({
        hasRQTH: z.boolean().describe('Whether the candidate has RQTH (handicap worker status)'),
        mentions: z
          .array(z.string())
          .default([])
          .describe(
            'Additional factual mentions: vehicle ("Véhiculé"), immediate availability ("Disponible immédiatement"), workstation adaptations, schedule constraints. NEVER duplicate information already in personalInfo (do NOT repeat driver license / "Permis", city, mobility radius, or hobbies here). NEVER put verification notes, uncertainty memos, or comments such as "à préciser", "à confirmer", or "période approximative".',
          ),
      })
      .default({ hasRQTH: false, mentions: [] })
      .describe('Complementary information about the candidate'),
  })
  .describe("The user's CV");

export type GeneratedCV = z.infer<typeof cvSchema>;

export type CVWithAllData = Prisma.CVGetPayload<{
  include: {
    experiences: true;
    languages: true;
    education: true;
  };
}>;

export type FullCV = Prisma.CVGetPayload<{
  omit: {
    interviewId: true;
    localeId: true;
    id: true;
    userId: true;
    template: true;
    templateParams: true;
    createdAt: true;
    updatedAt: true;
    status: true;
    validatedAt: true;
    deletedAt: true;
  };
  include: {
    experiences: { omit: { cvId: true; id: true } };
    personalExperiences: { omit: { cvId: true; id: true } };
    languages: { omit: { cvId: true; id: true } };
    education: { omit: { cvId: true; id: true } };
    locale: { select: { name: true; codeShort: true; codeLong: true } };
  };
}>;

export type PartialLocale = Pick<Locale, 'name' | 'codeShort' | 'codeLong'>;

export type FullCVWithTemplate = FullCV & {
  interviewId: string;
  template: string;
  templateParams: Prisma.JsonValue;
};

const cleanStr = (v: string | null | undefined): string => {
  if (v == null || v === 'null') return '';
  const trimmed = v.trim();
  if (isNotCommunicated(trimmed)) return '';
  return stripAiReflections(trimmed);
};
const cleanNullable = (v: string | null | undefined): string | null => {
  if (v == null || v === 'null') return null;
  const trimmed = v.trim();
  if (isNotCommunicated(trimmed)) return null;
  const cleaned = stripAiReflections(trimmed);
  return cleaned === '' ? null : cleaned;
};

export function transformGeneratedCVToFullCV(
  cv: GeneratedCV,
  sensitiveInfo: CVSensitiveInfo,
  locale: PartialLocale,
  defaultPictureBase64?: string | null,
): FullCV {
  const { personalInfo, experiences, education, skills: skillsAndLanguages, summary } = cv;
  const { languages, ...skills } = skillsAndLanguages;

  return {
    ...cv,
    ...personalInfo,
    ...sensitiveInfo,
    pictureBase64:
      defaultPictureBase64 || (cv as { pictureBase64?: string | null }).pictureBase64 || null,
    summary: summary ? stripAiReflections(summary) : '',
    isReconversion: cv.isReconversion ?? false,
    location: cleanStr(personalInfo.location),
    mobilityRadius: personalInfo.mobility?.radius ?? null,
    mobilityRange: personalInfo.mobility?.range ?? null,
    postalCode: cleanNullable(personalInfo.postalCode),
    jobTitle: cleanStr(personalInfo.jobTitle),
    hasDriverLicense: personalInfo.hasDriverLicense || false,
    driverLicenseTypes: personalInfo.driverLicenseTypes ?? null,
    isMobile: personalInfo.mobility?.isMobile ?? false,
    openToTraining: personalInfo.openToTraining ?? false,
    gender: personalInfo.gender ?? null,
    hobbiesAndInterests: (personalInfo.hobbiesAndInterests || [])
      .map(h => cleanStr(h))
      .filter(Boolean),
    experiences: experiences.professional.map(exp => ({
      location: cleanStr(exp.location),
      company: cleanStr(exp.company),
      title: stripDatesAndMetadataFromTitle(cleanStr(exp.title)),
      missions: cleanStr(exp.missions),
      startDate: cleanStr(exp.startDate),
      endDate: cleanNullable(exp.endDate),
    })),
    personalExperiences: experiences.personal.map(exp => ({
      title: stripDatesAndMetadataFromTitle(cleanStr(exp.title)),
      description: cleanStr(exp.description),
      location: exp.location ? cleanStr(exp.location) : null,
      startDate: cleanStr(exp.startDate),
      endDate: cleanNullable(exp.endDate),
    })),
    languages: languages.map(lang => ({
      language: cleanStr(lang.language),
      level: cleanStr(lang.level),
    })),
    education: [
      ...(education.schools || []).map(school => ({
        school: cleanStr(school.school),
        name: cleanNullable(school.name ? stripDatesAndMetadataFromTitle(school.name) : null),
        degree: cleanStr(school.degree ? stripDatesAndMetadataFromTitle(school.degree) : null),
        location: school.location ? cleanStr(school.location) : null,
        acquiredSkills: (school.acquiredSkills || []).map(s => cleanStr(s)).filter(Boolean),
        degreeStartedAt: cleanNullable(school.degreeStartedAt),
        degreeObtainedAt: cleanNullable(school.degreeObtainedAt),
      })),
      ...(education.certifications || []).map(certif => ({
        school: cleanStr(certif.issuingOrganization),
        name: null,
        degree: cleanStr(certif.certification ? stripDatesAndMetadataFromTitle(certif.certification) : null),
        location: null,
        acquiredSkills: (certif.acquiredSkills || []).map(s => cleanStr(s)).filter(Boolean),
        degreeStartedAt: null,
        degreeObtainedAt: cleanNullable(certif.certificationObtainedAt),
      })),
    ],
    competences: (skills.skills || (skills as unknown as { competences?: string[] }).competences || []).map(s => cleanStr(s)).filter(Boolean),
    strengths: (skills.strengths || []).map(s => cleanStr(s)).filter(Boolean),
    preferedTasks: (skills.preferedTasks || []).map(t => cleanStr(t)).filter(Boolean),
    notPreferedTasks: (skills.notPreferedTasks || []).map(t => cleanStr(t)).filter(Boolean),
    locale,
    hasRQTH: cv.complementaryInfo.hasRQTH,
    complementaryMentions: (cv.complementaryInfo.mentions || [])
      .filter(mention => Boolean(mention) && !isInternalAiNote(mention))
      .map(mention => stripAiReflections(mention))
      .filter(
        mention =>
          Boolean(mention) &&
          !isInternalAiNote(mention) &&
          !isDuplicateMention(mention, {
            hasDriverLicense: personalInfo.hasDriverLicense,
            driverLicenseTypes: personalInfo.driverLicenseTypes,
            location: personalInfo.location,
            postalCode: personalInfo.postalCode,
            isMobile: personalInfo.mobility?.isMobile,
            mobilityRadius: personalInfo.mobility?.radius,
            mobilityRange: personalInfo.mobility?.range,
          }),
      ),
  };
}

export const getGeneratedCVFromCV = (
  cv: CV & {
    experiences: Experience[];
    education: Education[];
    languages: Language[];
    personalExperiences: PersonalExperience[];
  },
): GeneratedCV => {
  const professional = (cv.experiences || []).map(exp => ({
    company: exp.company ?? '',
    location: exp.location ?? '',
    title: exp.title ?? '',
    missions: exp.missions ?? '',
    startDate: exp.startDate ?? null,
    endDate: exp.endDate ?? null,
  }));

  const personal = (cv.personalExperiences || []).map(p => ({
    description: p.description ?? null,
    title: p.title ?? '',
    location: p.location ?? null,
    startDate: p.startDate ?? null,
    endDate: p.endDate ?? null,
  }));

  const schools = (cv.education || []).map(e => ({
    degree: e.degree ?? null,
    school: e.school ?? '',
    name: e.name ?? '',
    location: e.location ?? null,
    acquiredSkills: e.acquiredSkills ?? [],
    degreeStartedAt: e.degreeStartedAt ?? null,
    degreeObtainedAt: e.degreeObtainedAt ?? null,
  }));

  const languages = (cv.languages || []).map(l => ({
    language: l.language ?? '',
    level: l.level ?? '',
  }));

  const personalInfo = {
    postalCode: cv.postalCode ?? null,
    location: cv.location ?? null,
    jobTitle: cv.jobTitle ?? '',
    hasDriverLicense: cv.hasDriverLicense ?? false,
    driverLicenseTypes: cv.driverLicenseTypes ?? null,
    openToTraining: cv.openToTraining ?? false,
    gender: cv.gender ?? null,
    hobbiesAndInterests: cv.hobbiesAndInterests ?? null,
    mobility: {
      isMobile: cv.isMobile ?? false,
      range: cv.mobilityRange ?? null,
      radius: (cv.mobilityRadius as unknown as number) ?? null,
    },
  } as GeneratedCV['personalInfo'];

  const skills = {
    skills: cv.competences || [],
    strengths: cv.strengths || [],
    preferedTasks: cv.preferedTasks || [],
    notPreferedTasks: cv.notPreferedTasks || [],
    languages,
  } as GeneratedCV['skills'];

  const education = {
    schools,
    certifications: [],
  } as GeneratedCV['education'];

  return {
    personalInfo,
    experiences: { professional, personal },
    skills,
    education,
    isCompleted: false,
    summary: cv.summary ?? null,
    question: null,
    isReconversion: cv.isReconversion ?? false,
    complementaryInfo: {
      hasRQTH: cv.hasRQTH ?? false,
      mentions: cv.complementaryMentions ?? [],
    },
  };
};

export const cvEditingSectionSchema = z.enum([
  'personalInfos',
  'competences',
  'languages',
  'experiences',
  'personalExperiences',
  'education',
  'hobbiesAndInterests',
  'summary',
  'complementaryInfo',
  'strengths',
  'preferedTasks',
  'notPreferedTasks',
]);

export type CVEditingSection = z.infer<typeof cvEditingSectionSchema>;

export const cvEditParamsSchema = z.object({
  userId: z.string(),
  sectionType: cvEditingSectionSchema,
  sectionContent: z.unknown().optional(), // Content of the specific section being edited
  /** Language of the CV being edited (chosen by the candidate). Optional:
   * the server falls back to the user's locale when absent. */
  localeCode: z.string().optional(),
});

export type CVEditParams = z.infer<typeof cvEditParamsSchema>;

/** WebSocket message for content updates from the client (CV edit flow) */
export interface ContentUpdateMessage {
  type: 'content_update';
  sectionContent?: unknown;
}

export const cvSectionValidationMap = {
  personalInfos: z.object({ content: cvPersonalInfoSchema }),
  experiences: z.object({ content: z.array(cvExperienceSchema) }),
  personalExperiences: z.object({ content: z.array(cvPersonalExperienceSchema) }),
  education: z.object({ content: z.array(cvSchoolSchema) }),
  competences: z.object({ content: z.array(z.string()) }),
  languages: z.object({ content: z.array(cvLanguageSchema) }),
  hobbiesAndInterests: z.object({ content: z.array(z.string()) }),
  summary: z.object({ content: z.string() }),
  complementaryInfo: z.object({
    content: z.object({
      hasRQTH: z.boolean(),
      complementaryMentions: z.array(z.string()),
    }),
  }),
  strengths: z.object({ content: z.array(z.string()) }),
  preferedTasks: z.object({ content: z.array(z.string()) }),
  notPreferedTasks: z.object({ content: z.array(z.string()) }),
};

export const cvSectionValidationSchema = z.object(cvSectionValidationMap);

export type CVSection = z.infer<typeof cvSectionValidationSchema>;

export const getCVCurrentSection = (
  cvSection: CVSection,
  currentSection: CVEditingSection,
): Partial<FullCV> => {
  switch (currentSection) {
    case 'personalInfos':
      const {
        location,
        jobTitle,
        hasDriverLicense,
        driverLicenseTypes,
        openToTraining,
        gender,
        mobility,
      } = cvSection.personalInfos.content;

      return {
        postalCode: cleanNullable(cvSection.personalInfos.content.postalCode),
        location: cleanStr(location),
        jobTitle: cleanStr(jobTitle),
        hasDriverLicense: hasDriverLicense || false,
        driverLicenseTypes: driverLicenseTypes ?? null,
        isMobile: mobility.isMobile ?? false,
        openToTraining: openToTraining ?? false,
        gender: gender ?? null,
        mobilityRadius: mobility.radius,
        mobilityRange: mobility.range,
      };
    case 'competences':
      return { competences: cvSection.competences.content };
    case 'languages':
      return {
        languages: cvSection.languages.content.map(language => ({
          language: cleanStr(language.language),
          level: cleanStr(language.level),
        })),
      };
    case 'experiences':
      return {
        experiences: cvSection.experiences.content.map(experience => ({
          company: cleanStr(experience.company),
          location: cleanStr(experience.location),
          title: stripDatesAndMetadataFromTitle(cleanStr(experience.title)),
          missions: experience.missions,
          startDate: cleanStr(experience.startDate),
          endDate: cleanNullable(experience.endDate),
        })),
      };
    case 'personalExperiences':
      return {
        personalExperiences: cvSection.personalExperiences.content.map(personalExperience => ({
          title: stripDatesAndMetadataFromTitle(cleanStr(personalExperience.title)),
          description: cleanStr(personalExperience.description),
          location: personalExperience.location ?? null,
          startDate: cleanStr(personalExperience.startDate),
          endDate: cleanNullable(personalExperience.endDate),
        })),
      };
    case 'education':
      return {
        education: cvSection.education.content.map(education => ({
          school: cleanStr(education.school),
          degree: cleanStr(education.degree ? stripDatesAndMetadataFromTitle(education.degree) : null),
          name: cleanNullable(education.name ? stripDatesAndMetadataFromTitle(education.name) : null),
          location: education.location ?? null,
          acquiredSkills: education.acquiredSkills || [],
          degreeStartedAt: cleanNullable(education.degreeStartedAt),
          degreeObtainedAt: cleanNullable(education.degreeObtainedAt),
        })),
      };
    case 'hobbiesAndInterests':
      return { hobbiesAndInterests: cvSection.hobbiesAndInterests.content };
    case 'summary':
      return { summary: cvSection.summary.content };
    case 'complementaryInfo':
      return {
        hasRQTH: cvSection.complementaryInfo.content.hasRQTH,
        complementaryMentions: cvSection.complementaryInfo.content.complementaryMentions,
      };
    case 'strengths':
      return { strengths: cvSection.strengths.content };
    case 'preferedTasks':
      return { preferedTasks: cvSection.preferedTasks.content };
    case 'notPreferedTasks':
      return { notPreferedTasks: cvSection.notPreferedTasks.content };
    default:
      throw new Error(`Unknown section: ${currentSection}`);
  }
};

export type CVListItemDto = {
  id: string;
  interviewId: string;
  jobTitle: string;
  firstName: string;
  lastName: string;
  createdAt: string;
  updatedAt: string;
  pictureBase64: string | null;
  templateId?: string;
  accentColor?: string;
};

export type CVWithInterviewUser = Prisma.CVGetPayload<{
  include: {
    interview: {
      include: {
        user: {
          select: {
            email: true;
          };
        };
      };
    };
  };
}>;

export * from './cv-schema-compliance';
